import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Grid from "veneer/Grid";
import Stack from "veneer/Stack";
import ToggleButtonGroup, { ToggleButton } from "veneer/ToggleButtonGroup";

import { osChrome } from "cortex/ui/src/fleet/constant/osPlatform";
import {
  LOAD_DEVICE_RESOURCES,
  unloadDeviceResourcesAction,
} from "cortex/ui/src/fleet/redux-store/ui-state/device-resources/actions";
import { resourcesSelector } from "cortex/ui/src/fleet/redux-store/ui-state/device-resources/selectors";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import { applicationsSelector } from "cortex/ui/src/redux-store/applications/selectors";
import { networksSelector } from "cortex/ui/src/redux-store/networks/selectors";
import { storesReady } from "cortex/ui/src/redux-store/utils";
import { NavLoader } from "cortex/ui/src/ui/loader";
import { Panel } from "cortex/ui/src/view/layout/Panel";

import DiskChart from "./charts/DiskChart";
import MemoryChart from "./charts/MemoryChart";
import NetworkChart from "./charts/NetworkChart";
import TemperatureChart from "./charts/TemperatureChart";
import { DevicesLocationMap } from "./map";
import { ChartPoint, Device, DeviceStatus } from "./model";
import ScreenshotWidget from "./ScreenshotWidget";
import { canTakeWebcamSnapshotForDevice } from "./utils";

type ResourceType = "all" | "disk" | "memory" | "network" | "temperature";
type PanelResourceType = Exclude<ResourceType, "all">;
type ResourceWindowHours = 1 | 24 | 168;
type TemperatureUnit = "c" | "f";
type DeviceResourceKey = Exclude<ResourceType, "all">;
type DeviceResources = Partial<
  Record<DeviceResourceKey, ReadonlyArray<ChartPoint>>
>;

interface DeviceDetailsProps {
  device: Device | null;
  deviceId: string;
  deviceStatus: DeviceStatus;
  storesLoaded: boolean;
}

interface DateControlsProps {
  disabled?: boolean;
  hours: number;
  onSelectHours: (type: PanelResourceType, hours: ResourceWindowHours) => void;
  onTemperatureUnitChange?: (unit: TemperatureUnit) => void;
  temperatureUnit?: TemperatureUnit;
  type: PanelResourceType;
}

const UNIT_LABELS: Record<TemperatureUnit, string> = {
  c: "°C",
  f: "°F",
};

function DateControls({
  disabled,
  hours,
  onSelectHours,
  onTemperatureUnitChange,
  temperatureUnit,
  type,
}: DateControlsProps) {
  return (
    <Stack direction="row" gap={1} mb={1}>
      <ToggleButtonGroup
        dataTestId={`${type}-hours-toggle`}
        onChange={(value) => {
          const normalizedValue = value == null ? undefined : String(value);

          if (
            normalizedValue === "1" ||
            normalizedValue === "24" ||
            normalizedValue === "168"
          ) {
            onSelectHours(type, Number(normalizedValue) as ResourceWindowHours);
          }
        }}
      >
        <ToggleButton
          {...({ disabled } as any)}
          dataTestId={`${type}-hours-toggle-168`}
          key={`${type}-168`}
          selected={hours === 168}
          value={168}
        >
          Last 7 Days
        </ToggleButton>
        <ToggleButton
          {...({ disabled } as any)}
          dataTestId={`${type}-hours-toggle-24`}
          key={`${type}-24`}
          selected={hours === 24}
          value={24}
        >
          Last 24 Hours
        </ToggleButton>
        <ToggleButton
          {...({ disabled } as any)}
          dataTestId={`${type}-hours-toggle-1`}
          key={`${type}-1`}
          selected={hours === 1}
          value={1}
        >
          Last Hour
        </ToggleButton>
      </ToggleButtonGroup>
      {type === "temperature" && onTemperatureUnitChange && (
        <ToggleButtonGroup
          onChange={(value) => {
            if (value === "c" || value === "f") {
              onTemperatureUnitChange(value);
            }
          }}
        >
          {(Object.keys(UNIT_LABELS) as TemperatureUnit[]).map((unit) => (
            <ToggleButton
              key={unit}
              selected={temperatureUnit === unit}
              value={unit}
            >
              {UNIT_LABELS[unit]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
    </Stack>
  );
}

export default function DeviceDetails({
  device,
  deviceId,
  deviceStatus,
  storesLoaded: parentStoresLoaded,
}: DeviceDetailsProps) {
  const dispatch = useAppDispatch();
  const [hoursByType, setHoursByType] = useState<
    Partial<Record<PanelResourceType, ResourceWindowHours>>
  >({});
  const [isLoadingResources, setIsLoadingResources] = useState(false);
  const [temperatureUnit, setTemperatureUnit] = useState<TemperatureUnit>("c");
  const applications = useAppSelector(applicationsSelector);
  const resources = useAppSelector(resourcesSelector) as DeviceResources;
  const networksById = useAppSelector(networksSelector);
  const resourceCacheRef = useRef<{
    disk: ReadonlyArray<ChartPoint>;
    memory: ReadonlyArray<ChartPoint>;
    network: ReadonlyArray<ChartPoint>;
    temperature: ReadonlyArray<ChartPoint>;
  }>({
    disk: [],
    memory: [],
    network: [],
    temperature: [],
  });
  const storesLoaded = useAppSelector(
    (state) =>
      storesReady((state as any).global.applications) && parentStoresLoaded,
  );

  const fetchResources = useCallback(
    async (type: ResourceType = "all", hours = 1) => {
      if (!deviceId) {
        return;
      }

      setIsLoadingResources(true);

      try {
        await createPromiseApiAction(dispatch, LOAD_DEVICE_RESOURCES, [
          deviceId,
          type,
          hours,
        ]);

        if (type !== "all") {
          setHoursByType((prev) => ({
            ...prev,
            [type]: hours as ResourceWindowHours,
          }));
        }
      } finally {
        setIsLoadingResources(false);
      }
    },
    [deviceId, dispatch],
  );

  useEffect(() => {
    void fetchResources("all", 1);
  }, [fetchResources]);

  useEffect(() => {
    return () => {
      dispatch(unloadDeviceResourcesAction);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!resources) {
      return;
    }

    const nextResources = {
      disk: Array.isArray(resources.disk) ? resources.disk : [],
      memory: Array.isArray(resources.memory) ? resources.memory : [],
      network: Array.isArray(resources.network) ? resources.network : [],
      temperature: Array.isArray(resources.temperature)
        ? resources.temperature
        : [],
    };

    (Object.keys(nextResources) as Array<keyof typeof nextResources>).forEach(
      (key) => {
        if (nextResources[key].length > 0) {
          resourceCacheRef.current[key] = nextResources[key];
        }
      },
    );
  }, [resources]);

  const { disk, memory, network, temperature } = resourceCacheRef.current;

  const online = Boolean(deviceStatus?.online);

  const isChromeOS = useMemo(() => {
    return Boolean(device?.os_platform === osChrome.id);
  }, [device]);
  const canTakeWebcamSnapshot = useMemo(() => {
    return canTakeWebcamSnapshotForDevice(device, networksById);
  }, [device, networksById]);

  const handleSelectHours = useCallback(
    (type: Exclude<ResourceType, "all">, hours: ResourceWindowHours) => {
      void fetchResources(type, hours);
    },
    [fetchResources],
  );

  if (!storesLoaded) {
    return <NavLoader.Proxy />;
  }

  if (!device) {
    return null;
  }

  return (
    <>
      <Stack>
        <Grid container spacing={2}>
          <Grid item md={6} xs={12}>
            <DevicesLocationMap devices={[device]} />
          </Grid>
          <Grid item md={6} xs={12}>
            <ScreenshotWidget
              canTakeWebcamSnapshot={canTakeWebcamSnapshot}
              device={device}
              isChromeOS={isChromeOS}
              online={online}
            />
          </Grid>
        </Grid>
      </Stack>
      <Panel title="Memory Usage per Application (MB)">
        <DateControls
          disabled={isLoadingResources}
          hours={hoursByType.memory ?? 1}
          onSelectHours={handleSelectHours}
          type="memory"
        />
        <MemoryChart applications={applications} data={memory} />
      </Panel>
      <Panel title="Network Usage">
        <DateControls
          disabled={isLoadingResources}
          hours={hoursByType.network ?? 1}
          onSelectHours={handleSelectHours}
          type="network"
        />
        <NetworkChart data={network} />
      </Panel>
      <Panel title="Disk Usage Percentage">
        <DateControls
          disabled={isLoadingResources}
          hours={hoursByType.disk ?? 1}
          onSelectHours={handleSelectHours}
          type="disk"
        />
        <DiskChart data={disk} />
      </Panel>
      <Panel title="Device Temperature">
        <DateControls
          disabled={isLoadingResources}
          hours={hoursByType.temperature ?? 1}
          onSelectHours={handleSelectHours}
          onTemperatureUnitChange={setTemperatureUnit}
          temperatureUnit={temperatureUnit}
          type="temperature"
        />
        <TemperatureChart
          data={temperature}
          temperatureUnit={temperatureUnit}
        />
      </Panel>
    </>
  );
}
